import React, { useEffect, useRef } from "react";
import { useVirtualizer, defaultRangeExtractor } from "@tanstack/react-virtual";
import cx from "classnames";
import styles from "./VirtualList.module.css";

const STICKY_OFFSET = 4;

/**
 * @typedef {import("@tanstack/react-virtual").VirtualItem} VirtualItem
 * @typedef {import("react").ReactElement} ReactElement
 * @typedef {import("@tanstack/react-virtual").measureElement} measureElement
 */

/**
 * Outermost wrapper to be used by the virtualizer on a per-item basis to position and size row.
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child elements to render inside the row.
 * @param {number} props.index - The index of the current item.
 * @param {measureElement} props.measure - Function to measure the element's size.
 * @param {boolean} props.sticky - Indicates if the item is sticky.
 * @param {boolean} props.activeSticky - Indicates if the item is the active sticky item.
 * @param {number} props.start - The starting position for the item.
 * @param {number} props.stickyOffset - The offset for sticky items.
 * @param {boolean} props.header - Indicates if the item is a header.
 * @returns {React.ReactElement} The rendered row wrapper element.
 *
 */
const RowWrapper = ({ children, index, sticky, activeSticky, start, stickyOffset = 0, header }) => {
  const style = {};

  if (!activeSticky) {
    const adjustedStart = start + stickyOffset;
    style.transform = `translateY(${adjustedStart}px)`;
  }

  return (
    <div
      style={style}
      data-index={index}
      className={cx([
        styles.listItem,
        { [styles.sticky]: sticky },
        { [styles.active]: activeSticky },
        { [styles.headerItem]: header },
      ])}
    >
      {children}
    </div>
  );
};

const calculateStickyOffset = (index, items, isSticky) =>
  items.reduce(
    (offset, item) =>
      isSticky(item.index) && item.index < index ? offset + STICKY_OFFSET : offset,
    0,
  );

/**
 * Renders a virtual list based on length (of data), using the row renderer as it's child to render each row. Wraps general/basic usage of the tanstack virtualizer.
 * @param {Object} props
 * @param {(props: VirtualItem) => ReactElement} props.children - a callback that takes in a virtual item and returns a reactElement, or effectively a react component that accepts the same props as VirtualItem. This can be changed to be a prop if it causes confusion.
 * @param {(typeof defaultRangeExtractor)=} props.rangeExtractor - a callback returning a list of indices of items to be rendered in the current window to overwrite default behavior.
 * @param {(index: number) => boolean} props.isSticky - check if item is a sticky element (at all)
 * @param {(index: number) => boolean} props.isActiveSticky - check if item is an active sticky element
 * @param {(index: number) => number} props.estimateSize - function to estimate size of an item
 * @param {import("@tanstack/react-virtual").measureElement} props.measureElement - a callback used to measure each row element, returning the size or null to use the previous value.
 * @param {number} props.size - list length. Number of items in the list to be rendered
 * @param {number} props.startIndex - Index to scroll to on mount, useful for switching between panels where list may be unmounted.
 * @returns {ReactElement}
 */
const VirtualList = ({
  children: rowRenderer,
  estimateSize = () => 32,
  getItemKey,
  isSticky = () => false,
  isActiveSticky = () => false,
  rangeExtractor,
  measureElement,
  size,
  startIndex = -1,
}) => {
  const listRef = useRef(null);

  const virtualizer = useVirtualizer({
    count: size,
    getScrollElement: () => listRef.current,
    estimateSize: (index) => {
      const baseSize = estimateSize(index);
      return isSticky(index) ? baseSize + STICKY_OFFSET : baseSize;
    },
    enabled: true,
    overscan: 10,
    rangeExtractor: rangeExtractor ?? defaultRangeExtractor,
    measureElement,
    getItemKey,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // scroll on mount
    virtualizer.scrollToIndex(startIndex < -1 ? 0 : startIndex, { align: "center" });
  }, []);

  const items = virtualizer.getVirtualItems();

  return (
    <div ref={listRef} className={styles.list}>
      <div
        className={styles.listContent}
        style={{
          height: virtualizer.getTotalSize(),
        }}
      >
        {items.map((virtualItem) => {
          const rendered = rowRenderer({ ...virtualItem, measure: virtualizer.measureElement });
          // Use static property instead of function.name (which gets minified in production)
          const isHeader = rendered?.type?.isHeader === true;
          return (
            <RowWrapper
              activeSticky={isActiveSticky(virtualItem.index)}
              index={virtualItem.index}
              key={virtualItem.key}
              measure={virtualizer.measureElement}
              size={virtualItem.size}
              start={virtualItem.start}
              sticky={isSticky(virtualItem.index)}
              stickyOffset={calculateStickyOffset(virtualItem.index, items, isSticky)}
              header={isHeader}
            >
              {rendered}
            </RowWrapper>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualList;
